/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import type {
  ChannelType,
  Message,
  PackageStats,
  ParsedAttachment,
  ProcessedChannel,
} from "@/types";
import JSONbig from "json-bigint";
import { DateRange } from "react-day-picker";

const JSONbigParser = JSONbig({ storeAsString: true });

interface RawChannelData {
  id: string;
  type: string;
  name?: string;
  recipients?: string[];
  guild?: {
    id: string;
    name: string;
  };
  icon_hash?: string;
}

export interface ProcessedData {
  channels: ProcessedChannel[];
  stats: PackageStats;
}

interface LoadedChannel {
  channelData: RawChannelData;
  messages: Message[];
  rawName: string;
}

function parseJsonBig<T>(text: string): T {
  return JSONbigParser.parse(text) as T;
}

function isFileSystemFileHandle(
  value: File | FileSystemFileHandle,
): value is FileSystemFileHandle {
  return typeof (value as FileSystemFileHandle).getFile === "function";
}

async function readText(source: File | FileSystemFileHandle): Promise<string> {
  if (isFileSystemFileHandle(source)) {
    const file = await source.getFile();
    return file.text();
  }
  return source.text();
}

function getConcurrency(): number {
  const hardwareConcurrency =
    (typeof navigator !== "undefined" && navigator.hardwareConcurrency) || 4;
  return Math.max(2, Math.min(6, hardwareConcurrency));
}

async function processInBatches<T>(
  ids: string[],
  concurrency: number,
  loader: (id: string) => Promise<T | null>,
  onLoaded: (value: T) => void,
): Promise<void> {
  for (let i = 0; i < ids.length; i += concurrency) {
    const batchIds = ids.slice(i, i + concurrency);

    const batchResults = await Promise.all(batchIds.map((id) => loader(id)));

    batchResults.forEach((result) => {
      if (result) onLoaded(result);
    });

    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}

class PackageProcessor {
  private stats: PackageStats;
  private processedChannels: ProcessedChannel[] = [];
  private earliestMessageDate?: Date;
  private latestMessageDate?: Date;
  private dmChannelsRecipients: {
    channelId: string;
    recipients: string[];
  }[] = [];

  constructor() {
    this.stats = {
      totalMessages: 0,
      totalAttachments: 0,
      uniqueServers: new Set<string>(),
      channelTypes: new Set<string>(),
      serverCount: 0,
      channelCount: 0,
      dateRange: {
        start: new Date(),
        end: new Date(),
      },
    };
  }

  processChannelData(
    channelData: RawChannelData,
    rawName: string,
    messages: Message[],
  ) {
    if (messages.length === 0) return;

    const dates = getMessageDateRange(messages);
    if (dates) this.updateDateRangeFromRange(dates);

    if (
      channelData.type === "DM" &&
      channelData.recipients &&
      channelData.recipients.length === 2
    ) {
      this.dmChannelsRecipients.push({
        channelId: channelData.id,
        recipients: channelData.recipients,
      });
    }

    const processedChannel = processChannel(
      channelData,
      rawName,
      messages,
      dates,
    );

    this.stats.totalMessages += messages.length;
    this.stats.totalAttachments += messages.filter((m) => m.Attachments).length;
    this.stats.channelTypes.add(channelData.type);

    if (processedChannel.serverName) {
      this.stats.uniqueServers.add(processedChannel.serverName);
    }

    this.processedChannels.push(processedChannel);
  }

  finalize(): ProcessedData {
    this.processedChannels.sort(
      (a, b) => b.messages.length - a.messages.length,
    );

    this.stats.channelCount = this.processedChannels.length;
    this.stats.serverCount = this.stats.uniqueServers.size;

    const currentUserId = this.determineCurrentUserId();

    if (currentUserId) {
      this.processedChannels.forEach((channel) => {
        if (channel.type === "DM" && channel.metadata.recipients) {
          channel.currentUserId = currentUserId;

          const otherRecipient = channel.metadata.recipients.find(
            (id) => id !== currentUserId && id !== "Deleted User",
          );
          if (otherRecipient) {
            channel.recipientId = otherRecipient;
          }
        }
      });
    }

    if (this.earliestMessageDate && this.latestMessageDate) {
      this.stats.dateRange = {
        start: this.earliestMessageDate,
        end: this.latestMessageDate,
      };
    }

    return {
      channels: this.processedChannels,
      stats: this.stats,
    };
  }

  private updateDateRangeFromRange(range: DateRange) {
    const start = range.from;
    const end = range.to;

    if (start) {
      if (!this.earliestMessageDate || start < this.earliestMessageDate) {
        this.earliestMessageDate = start;
      }
      if (!this.latestMessageDate || start > this.latestMessageDate) {
        this.latestMessageDate = start;
      }
    }

    if (end) {
      if (!this.earliestMessageDate || end < this.earliestMessageDate) {
        this.earliestMessageDate = end;
      }
      if (!this.latestMessageDate || end > this.latestMessageDate) {
        this.latestMessageDate = end;
      }
    }
  }

  private determineCurrentUserId(): string | undefined {
    if (this.dmChannelsRecipients.length === 0) return undefined;

    const recipientCounts = new Map<string, number>();

    this.dmChannelsRecipients.forEach(({ recipients }) => {
      recipients.forEach((id) => {
        if (id !== "Deleted User") {
          recipientCounts.set(id, (recipientCounts.get(id) || 0) + 1);
        }
      });
    });

    if (this.dmChannelsRecipients.length > 1) {
      let maxCount = 0;
      let currentUserId: string | undefined;

      recipientCounts.forEach((count, id) => {
        if (count > maxCount) {
          maxCount = count;
          currentUserId = id;
        }
      });

      return currentUserId;
    }

    if (this.dmChannelsRecipients.length === 1) {
      const singleDM = this.dmChannelsRecipients[0];
      if (singleDM.recipients.includes("Deleted User")) {
        return singleDM.recipients.find((id) => id !== "Deleted User");
      }
    }

    return undefined;
  }
}

function parseAttachments(attachmentsString?: string): ParsedAttachment[] {
  if (!attachmentsString) return [];

  return attachmentsString
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((url) => {
      try {
        const cleanUrl = url.trim();
        const urlObj = new URL(cleanUrl);
        const filename = urlObj.pathname.split("/").pop() || "attachment";
        return { url: cleanUrl, filename };
      } catch {
        console.warn("Invalid attachment URL:", url);
        return null;
      }
    })
    .filter(
      (attachment): attachment is ParsedAttachment => attachment !== null,
    );
}

async function loadIndexFromHandle(
  messagesFolder: FileSystemDirectoryHandle,
): Promise<Record<string, string>> {
  const indexHandle = await messagesFolder.getFileHandle("index.json");
  const indexContent = await readText(indexHandle);
  return parseJsonBig<Record<string, string>>(indexContent);
}

async function loadChannelFromDirectory(
  messagesDir: FileSystemDirectoryHandle,
  channelId: string,
  rawName: string,
): Promise<LoadedChannel | null> {
  try {
    const channelDir = await messagesDir.getDirectoryHandle(`c${channelId}`, {
      create: false,
    });

    const [channelHandle, messagesHandle] = await Promise.all([
      channelDir.getFileHandle("channel.json", { create: false }),
      channelDir.getFileHandle("messages.json", { create: false }),
    ]);

    const [channelContent, messagesContent] = await Promise.all([
      readText(channelHandle),
      readText(messagesHandle),
    ]);

    const channelData = parseJsonBig<RawChannelData>(channelContent);
    const messages = parseJsonBig<Message[]>(messagesContent);

    if (messages.length === 0) return null;

    return { channelData, messages, rawName };
  } catch (error) {
    console.error(`Error reading channel ${channelId}:`, error);
    return null;
  }
}

export async function runProcessing(
  messagesFolder: FileSystemDirectoryHandle,
): Promise<ProcessedData> {
  const index = await loadIndexFromHandle(messagesFolder);

  const processor = new PackageProcessor();
  const channelIds = Object.keys(index);
  const concurrency = getConcurrency();

  await processInBatches(
    channelIds,
    concurrency,
    (channelId) =>
      loadChannelFromDirectory(messagesFolder, channelId, index[channelId]),
    (loaded) =>
      processor.processChannelData(
        loaded.channelData,
        loaded.rawName,
        loaded.messages,
      ),
  );

  return processor.finalize();
}

function processChannel(
  channelData: RawChannelData,
  rawName: string,
  messages: Message[],
  dates: DateRange | undefined = getMessageDateRange(messages),
): ProcessedChannel {
  const processedMessages = messages.map((msg) => {
    const parsed = parseAttachments(msg.Attachments);
    return { ...msg, parsedAttachments: parsed };
  });

  const attachmentCount = processedMessages.reduce(
    (sum, msg) => sum + (msg.parsedAttachments?.length || 0),
    0,
  );

  const processed: ProcessedChannel = {
    id: channelData.id,
    type: channelData.type as ChannelType,
    displayName: "",
    originalName: rawName,
    messages: processedMessages,
    metadata: {
      ...channelData,
      rawIndexName: rawName,
    },
    searchableText: "",
    messageCount: messages.length,
    attachmentCount,
    dateRange: {
      start: dates?.from || new Date(),
      end: dates?.to || new Date(),
    },
  };

  if (channelData.type === "DM") {
    const dmMatch = rawName.match(/^Direct Message with (.+?)(?:#0)?$/);
    const username = dmMatch
      ? dmMatch[1]
      : channelData.recipients?.[0] || "Unknown User";

    processed.displayName = `Direct Message with ${username}`;
    processed.participants = [username];
    processed.channelCategory = "Direct Messages";
  } else if (channelData.type === "GROUP_DM") {
    if (rawName.startsWith("Direct Message with ")) {
      const participantsStr = rawName.replace("Direct Message with ", "");
      const participants = participantsStr
        .split(", ")
        .map((p) => p.replace(/#0$/, ""));
      processed.displayName = `Group DM with ${participants.join(", ")}`;
      processed.participants = participants;
    } else {
      processed.displayName =
        channelData.name ||
        `Group DM with ${channelData.recipients?.join(", ") || "Unknown Users"}`;
      processed.participants = channelData.recipients;
    }
    processed.channelCategory = "Group Messages";
  } else if (channelData.guild) {
    processed.serverName = channelData.guild.name;

    if (rawName.startsWith("Unknown channel in")) {
      processed.displayName = "Unknown Channel";
      if (channelData.name) {
        processed.displayName = channelData.name;
      }
    } else {
      const categoryMatch = rawName.match(/^(.+?)(?: in (.+))?$/);
      if (categoryMatch) {
        const [, channelName, category] = categoryMatch;
        processed.displayName = channelName.trim();
        if (category && category !== channelData.guild.name) {
          processed.channelCategory = category.trim();
        }
      } else {
        processed.displayName = rawName;
      }
    }
  } else {
    processed.displayName = rawName;
  }

  processed.searchableText = sanitizeForSearch(
    [
      processed.displayName,
      processed.serverName,
      processed.channelCategory,
      processed.participants?.join(" "),
      processed.originalName,
      messages.map((m) => sanitizeForSearch(m.Contents)).join(" "),
    ]
      .filter(Boolean)
      .join(" "),
  );

  return processed;
}

function getMessageDateRange(messages: Message[]): DateRange | undefined {
  if (messages.length === 0) return undefined;

  let minDate = new Date(messages[0].Timestamp);
  let maxDate = new Date(messages[0].Timestamp);

  for (let i = 1; i < messages.length; i += 1) {
    const timestamp = new Date(messages[i].Timestamp);
    const time = timestamp.getTime();

    if (time < minDate.getTime()) minDate = timestamp;
    if (time > maxDate.getTime()) maxDate = timestamp;
  }

  return { from: minDate, to: maxDate };
}

export function sanitizeForSearch(text: string | undefined): string {
  if (!text) return "";

  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function findMatchesInText(
  haystack: string,
  needle: string,
): [number, number][] {
  const sanitizedHaystack = sanitizeForSearch(haystack);
  const sanitizedNeedle = sanitizeForSearch(needle);
  const matches: [number, number][] = [];

  let index = 0;
  while ((index = sanitizedHaystack.indexOf(sanitizedNeedle, index)) !== -1) {
    matches.push([index, index + sanitizedNeedle.length]);
    index += sanitizedNeedle.length;
  }

  return matches;
}

export async function runProcessingFromFiles(
  files: File[],
): Promise<ProcessedData> {
  interface ChannelFiles {
    channelId: string;
    channelJson?: File;
    messagesJson?: File;
  }

  const channelFilesMap = new Map<string, ChannelFiles>();
  let indexFile: File | undefined;

  for (const file of files) {
    const path = file.webkitRelativePath || file.name;

    if (path.endsWith("/index.json") || path === "index.json") {
      indexFile = file;
      continue;
    }

    const channelMatch = path.match(/\/c(\d+)\/(channel|messages)\.json$/);
    if (!channelMatch) continue;

    const channelId = channelMatch[1];
    const fileType = channelMatch[2];

    if (!channelFilesMap.has(channelId)) {
      channelFilesMap.set(channelId, { channelId });
    }

    const channelData = channelFilesMap.get(channelId)!;
    if (fileType === "channel" && !channelData.channelJson) {
      channelData.channelJson = file;
    } else if (fileType === "messages" && !channelData.messagesJson) {
      channelData.messagesJson = file;
    }
  }

  if (!indexFile) {
    throw new Error(
      'Could not find index.json. Please select the "Messages" folder from your Discord data package.',
    );
  }

  const indexContent = await readText(indexFile);
  const index = parseJsonBig<Record<string, string>>(indexContent);

  const processor = new PackageProcessor();
  const concurrency = getConcurrency();
  const channelIds = Array.from(channelFilesMap.keys());

  await processInBatches(
    channelIds,
    concurrency,
    async (channelId): Promise<LoadedChannel | null> => {
      const channelFiles = channelFilesMap.get(channelId);
      if (!channelFiles) return null;

      if (!channelFiles.channelJson || !channelFiles.messagesJson) {
        console.warn(`Skipping channel ${channelId}: missing files`);
        return null;
      }

      try {
        const [channelContent, messagesContent] = await Promise.all([
          readText(channelFiles.channelJson),
          readText(channelFiles.messagesJson),
        ]);

        const channelData = parseJsonBig<RawChannelData>(channelContent);
        const messages = parseJsonBig<Message[]>(messagesContent);

        if (messages.length === 0) return null;

        const rawName = index[channelId] || `Unknown Channel ${channelId}`;
        return { channelData, messages, rawName };
      } catch (error) {
        console.error(`Error processing channel ${channelId}:`, error);
        return null;
      }
    },
    (loaded) =>
      processor.processChannelData(
        loaded.channelData,
        loaded.rawName,
        loaded.messages,
      ),
  );

  return processor.finalize();
}
